import {PIXI} from '@colonist/pixi'
import {PlayerColor} from '@colonist/game-states'
import {UIGameSpriteHandler} from '../UIGameSpriteHandler'
import {PlayerValidator} from '@colonist/game-mechanic-validators'
import {drawPixiCircle} from '../ui_utils/PixiUtils'
import {GameColor} from '../GameColor'
import {UIGameManager} from './UIGameManager'

export class UIGamePlayerAvatarController {
    private readonly uiGameManager: UIGameManager
    private readonly cachedTextures: Map<string, PIXI.RenderTexture | PIXI.Texture>

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.cachedTextures = new Map()
    }

    private static getCacheKey(userId: string | undefined, color: PlayerColor, isActiveHumanSession: boolean): string {
        return `${userId}_${color}_${isActiveHumanSession}`
    }

    private getCachedTexture(cacheKey: string): PIXI.RenderTexture | PIXI.Texture | undefined {
        return this.cachedTextures.get(cacheKey)
    }

    private setCachedTexture(cacheKey: string, texture: PIXI.RenderTexture | PIXI.Texture) {
        this.cachedTextures.set(cacheKey, texture)
    }

    async getAvatarTexture(playerState: PlayerValidator): Promise<PIXI.RenderTexture | PIXI.Texture> {
        const userId = playerState.userState.userId
        const color = playerState.color
        const isActiveHumanSession = playerState.isActiveHumanSession

        const cacheKey = UIGamePlayerAvatarController.getCacheKey(userId, color, isActiveHumanSession)

        const cachedAvatar = this.getCachedTexture(cacheKey)
        if(cachedAvatar) return cachedAvatar

        const {texture: originalAvatar, applyCircleMask: applyCircleMask} = await UIGameSpriteHandler.getPlayerIcon(playerState)
        if(applyCircleMask) {
            const circularAvatar = this.createCircularAvatar(originalAvatar)
            this.setCachedTexture(cacheKey, circularAvatar)
            return circularAvatar
        }

        this.setCachedTexture(cacheKey, originalAvatar)
        return originalAvatar
    }

    private createCircularAvatar(originalAvatar: PIXI.Texture): PIXI.RenderTexture | PIXI.Texture {
        const avatarSize = 50
        const circularAvatar = PIXI.RenderTexture.create({
            width: avatarSize,
            height: avatarSize,
        })

        const sprite = new PIXI.Sprite(originalAvatar)
        sprite.width = avatarSize
        sprite.height = avatarSize

        const mask = drawPixiCircle(avatarSize / 2, avatarSize / 2, avatarSize / 2, GameColor.Color.PrimaryBlue0)
        sprite.mask = mask

        const tempContainer = new PIXI.Container()
        tempContainer.addChild(sprite)

        this.uiGameManager.canvasController.app.renderer.render(tempContainer, {renderTexture: circularAvatar})
        tempContainer.destroy({children: true})

        return circularAvatar
    }
}
